import { ReactNode, ReactElement } from 'react';
import _ from 'lodash';

import type { Props } from '@Utils/ReactProps';
import { DashboardManagerParent } from "../Dashboard/DashboardManagerParent";
import { WidgetDescriptor, TitleAlign, ITsViewFormat } from '@ModelViews/ModelViews';
import { isDefined, isNumeric } from '@Utils/Micro';
import { DateFormat } from '@Utils/Formats';
import { DebugEx } from '@Utils/DebugEx';
import { Plegma } from '@API/Api';
import type { IComponentSizeProp } from '@Primitive/Interfaces';
import type { SettingsToolbarSettings } from "../Primitive/SettingsList"
import type { LocalizationMap } from '@Primitive/Localization/Localization';
import type { IWidgetClassParent, IWidgetDialog } from './IWidgetClassParent';
import { ISchemaValidatorRsp } from '@Utils/JsonSchemaValidator';

export interface IWidgetDataValidateResp extends ISchemaValidatorRsp { }
export interface IWidget {
    WidgetType: WidgetType;

    /**
     * Widget Description
     */
    Description: IWidgetDescription;
    OverrideDescription?: Partial<IWidgetDescription>;

    /**
     * Back reference to react parent (e.g. DashboardManager)
     */
    Parent: IWidgetClassParent;

    /**
     * UI Rendering
     */
    Render: (key: string, width: number, height: number) => ReactNode;

    /**
     * UI toolbar Rendering
     */
    RenderToolbar: (key: string, toolbarHeight: number) => ReactNode;

    /**
     * UI Settings Rendering
     */
    RenderWidgetSettings: (key: string, width: number, height: number) => ReactNode;
    // --------------------------------------------------------------------------------------------
    GetDefaultValues: () => { [keys: string]: any };
    // --------------------------------------------------------------------------------------------
    GetWidgetDesc: () => IWidgetDescription;
    // --------------------------------------------------------------------------------------------
    ValidateValues: (key: string, value: any) => IWidgetDataValidateResp;
    // --------------------------------------------------------------------------------------------
    GetLocalizationDictionary: () => LocalizationMap;
    // --------------------------------------------------------------------------------------------
    UpdateLocalization: () => void;

    // ============================================================================== Wrappers to widget +++
    // --------------------------------------------------------------------------------------------
    RenderBase: (key: string, width: number, height: number) => ReactNode;
    // --------------------------------------------------------------------------------------------
    RenderBaseToolbar: (key: string, toolbarHeight: number) => ReactNode[];
    // --------------------------------------------------------------------------------------------
    ToolbarDynamicTooltip: () => string | ReactNode[];
    // --------------------------------------------------------------------------------------------
    RenderBaseSettings: (
        key: string,
        width: number,
        height: number,
        dialog: IWidgetDialog,
        onSaveSettings: (settings: WidgetThingGenericConfiguration) => void,
        revision?: number
    ) => ReactNode;
    // --------------------------------------------------------------------------------------------
    // =============================================================================== Wrappers to widget ---
}
export type IWidgetDictionary = { [key: string]: IWidget };
// ------------------------------------------------------------------------------------------------
export type IWidgetDescription = WidgetDescriptor;
// ------------------------------------------------------------------------------------------------
export function WidgetDescriptionToPlegmaThing(desc: IWidgetDescription): Plegma.Thing {
    return {
        Name: desc.Title,
        ThingKey: desc.Key,
        UIHints: { Description: desc.Title, IconURI: desc.IconURI },
    };
}
// ------------------------------------------------------------------------------------------------
export interface IWidgetGroupDesc {
    Name: string;
    Description: string;

    LeftIcon?: ReactElement<any>;

    // Type -> WidgetDescription
    Widgets: { [key: string]: IWidgetDescription }
}
// ------------------------------------------------------------------------------------------------
export interface IWidgetProps<T> extends Props<any>, IComponentSizeProp {
    /**
     * Props to pause any animation or internal play
     * this is helpful in the case of editing
     */
    inEditMode: boolean;

    /**
     * Private data of the Widget
     */
    WidgetData: T;

    /**
     * Default values
     */
    WidgetDefaultValues: { [key: string]: any };
}
// ------------------------------------------------------------------------------------------------
export interface IWidgetSettingsProps<T> extends Props<T>, IComponentSizeProp {
    /**
     * Link to web thing struct
     */
    ThingKey: string;
    NodeKey: string;
    LocalizationKey?: string;

    /**
     * Private data of the Widget
     */
    WidgetData: T;

    /**
     * Default values
     */
    WidgetDefaultValues: { [key: string]: any };

    /**
     * Updated data
     */
    onWidgetDataUpdate: (newData: T) => void;

    SettingListToolbar: SettingsToolbarSettings;
}
// ------------------------------------------------------------------------------------------------
export interface WidgetThingGenericConfiguration {
    Description: IWidgetDescription;
}
// ------------------------------------------------------------------------------------------------
export enum WidgetType {
    Base,
    Thing, // Yodiwo thing
}
export enum WidgetGroups {
    control = 'Control',
    userInput = 'User Input',
    view = 'View',
    image = 'Image',
    charts = 'Charts',
    gauges = 'Gauges',
    singleton = 'Singleton',
    layout = 'Layout',
    custom = 'Custom'
}
// ------------------------------------------------------------------------------------------------
export abstract class IWidgetClass<T> implements IWidget {
    public WidgetType: WidgetType;
    public Description: IWidgetDescription;
    public OverrideDescription?: Partial<IWidgetDescription>;
    public WidgetData: T;
    // --------------------------------------------------------------------------------------------
    public onWidgetDataUpdate?: (NewWidgetData: T) => void;
    public get Parent() { return DashboardManagerParent.GetDefaultInfo(); }
    // --------------------------------------------------------------------------------------------
    constructor(description: IWidgetDescription, data: T) {
        this.Description = this._migrateIWidgetDescription(description);

        this.OverrideDescription = {};
        this.WidgetType = WidgetType.Base;

        // Merge default data with saved one
        var migratedData = this.MigrateDefaultWidgetData(data);
        var defaultData = this.GetDefaultWidgetData();
        this.WidgetData = _.merge({}, defaultData, migratedData);
    }
    // --------------------------------------------------------------------------------------------
    /**
     * Used to migrate saved IWidgetDescription.
     * Can also be used for initializing values at new fields.
     * Note: not all fields need initialization
     * @param description
     */
    private _migrateIWidgetDescription(description: IWidgetDescription): IWidgetDescription {

        if (!isDefined(description.DefiesGrid))
            description.DefiesGrid = false;

        if (!isDefined(description.WidgetNoBackground))
            description.WidgetNoBackground = false;

        if (!isDefined(description.MobileOrder))
            description.MobileOrder = 0;

        if (!isDefined(description.StartWithLoading))
            description.StartWithLoading = false;

        if (!isDefined(description.IsHidden))
            description.IsHidden = false;

        if (!isDefined(description.MobileIsHidden))
            description.MobileIsHidden = false;

        if (isDefined(description.TitleStyle) &&
            isNumeric(description.TitleStyle.Alignment)) {
            if (description.TitleStyle.Alignment as any == 0)
                description.TitleStyle.Alignment = TitleAlign.Left;
            else if (description.TitleStyle.Alignment as any == 1)
                description.TitleStyle.Alignment = TitleAlign.Center;
            else
                description.TitleStyle.Alignment = TitleAlign.Right;
        }

        if (!isDefined(description.TitleStyle))
            description.TitleStyle = {
                Alignment: TitleAlign.Left,
                BackgroundColor: "rgb(255,255,255)",
                FontColor: "rgb(0,0,0)",
                Icon: "",
                TitleHeight: 25,
                FontAutoSize: true,
                FontSize: 14
            }
        else if (!isDefined(description.TitleStyle.FontAutoSize)) {
            description.TitleStyle.FontAutoSize = true;
            description.TitleStyle.FontSize = 14;
        }

        // Initialize track updates (if needs to). 
        if (!isDefined(description.TrackUpdates))
            description.TrackUpdates = {
                ShowLastUpdateTs: false,
                DateFormat: DateFormat.Short,
                ViewFormat: ITsViewFormat.Tooltip
            };

        if (!isDefined(description.WidgetPadding)) {
            description.WidgetPadding = {
                Fixed: false,
                Common: true,
                All: 0,
                Left: 0,
                Right: 0,
                Top: 0,
                Bottom: 0
            };
        }

        // Initialize outline (if needs to).
        if (!isDefined(description.Outline))
            description.Outline = {
                Thickness: 0,
                Color: 'rgb(189, 189, 189)'
            };
        // defer TextColor; default is null

        // initialize IconURI (if needs to)
        if (!isDefined(description.IconURI)) {
            import('./Factory').then((factory) => {
                const defaultWidgetDescription = factory.default.CreateWidgetThingDescDefault(description.Type);
                if (isDefined(defaultWidgetDescription))
                    description.IconURI = defaultWidgetDescription.IconURI;
                else
                    DebugEx.TraceCritical(`Could not get 'IconURI' for widget of type: '${description.Type}'.`);
            });
        }

        return description;
    }
    // --------------------------------------------------------------------------------------------
    abstract Render(key: string, width: number, height: number): ReactNode;
    // --------------------------------------------------------------------------------------------
    abstract RenderToolbar(key: string, toolbarHeight: number): ReactNode;
    // --------------------------------------------------------------------------------------------
    abstract ToolbarDynamicTooltip(): string | ReactNode[];
    // --------------------------------------------------------------------------------------------
    abstract RenderWidgetSettings(
        key: string,
        width: number,
        height: number
    ): ReactNode;
    // --------------------------------------------------------------------------------------------
    abstract WidgetDataUpdate(NewWidgetData: any): void;
    // --------------------------------------------------------------------------------------------
    abstract GetDefaultValues(): { [key: string]: any };
    // --------------------------------------------------------------------------------------------
    abstract ValidateValues(key: string, value: any): IWidgetDataValidateResp;
    // --------------------------------------------------------------------------------------------
    abstract GetDefaultWidgetData(): T;
    // --------------------------------------------------------------------------------------------
    abstract GetDefaultWidgetDataStatic(): T;
    // --------------------------------------------------------------------------------------------
    abstract MigrateDefaultWidgetData(data: T): T;
    // --------------------------------------------------------------------------------------------
    abstract GetLocalizationDictionary(): LocalizationMap;
    // --------------------------------------------------------------------------------------------
    abstract UpdateLocalization(): void;
    abstract GetLocalizedWidgetTitle(): string;
    // --------------------------------------------------------------------------------------------
    abstract GetWidgetDesc(): IWidgetDescription;
    // --------------------------------------------------------------------------------------------
    abstract GetDataPortKeyStatic(): string;
    // --------------------------------------------------------------------------------------------
    // Wrappers to `Render()`, `RenderToolbar()` and `RenderWidgetSettings`
    // --------------------------------------------------------------------------------------------
    abstract RenderBase(key: string, width: number, height: number): ReactNode;
    // --------------------------------------------------------------------------------------------
    abstract RenderBaseToolbar(key: string, toolbarHeight: number): ReactNode[];
    // --------------------------------------------------------------------------------------------
    abstract RenderBaseSettings(
        key: string,
        width: number,
        height: number,
        dialog: IWidgetDialog,
        onSaveSettings: (settings: WidgetThingGenericConfiguration) => void
    ): ReactNode;
    // --------------------------------------------------------------------------------------------
}